// ===================== Ultraweapons =====================
// A tier above evolutions: never offered on level-up cards (def.ultra), only
// claimed from slain ALPHA super bosses. Once owned they upgrade through the
// normal card flow like any other weapon.
import { defineWeapon } from '../core/registries.js';
import { CONFIG } from '../config.js';
import { TAU } from '../engine/math.js';
import { Audio } from '../engine/audio.js';

export const ULTRA_IDS = ['fangstorm', 'blacksun', 'prismlance'];

// ---------------------------------------------------------------- Fangstorm
defineWeapon({
  id: 'fangstorm', name: 'Fangstorm', icon: '⋔', tags: ['weapon'], ultra: true,
  desc: 'ULTRA — the sky bares its teeth: spectral fangs rain ruin on the horde.',
  maxLevel: 6,
  stats(l) {
    return {
      cooldown: Math.max(0.5, 1.05 - l * 0.09),
      damage: 30 + l * 11,
      count: 3 + l,
      area: 88 + l * 7,
    };
  },
  fire(game, s) {
    const p = game.player;
    const { count, dmgMult } = game.mult(s.count);
    const targets = game.enemiesInRadius(p.x, p.y, 760);
    for (let i = 0; i < count; i++) {
      const e = targets.length ? game.rng.pick(targets) : null;
      const x = e ? e.x : p.x + game.rng.range(-340, 340);
      const y = e ? e.y : p.y + game.rng.range(-340, 340);
      game.spawnProjectile({
        kind: 'aoe', x, y, dmg: game.baseDamage(s.damage) * dmgMult,
        maxR: s.area * p.areaMult, color: '#eaffff', life: 0.32, pierce: -1, rehit: 0,
        knockback: 60, applyElement: true, weaponId: 'fangstorm',
      });
      game.particles_spark(x, y, '#eaffff', 8);
    }
    Audio.shoot('holy');
  },
});

// ---------------------------------------------------------------- Black Sun
defineWeapon({
  id: 'blacksun', name: 'Black Sun', icon: '◉', tags: ['weapon'], ultra: true,
  desc: 'ULTRA — a collapsed star circles you, grinding everything it touches.',
  maxLevel: 6,
  stats(l) {
    return {
      cooldown: 4.4,
      damage: 24 + l * 8,
      count: 1 + Math.floor(l / 3),
      orbitRadius: 150 + l * 12,
      orbitSpeed: 1.7 + l * 0.08,
    };
  },
  fire(game, s) {
    const p = game.player;
    const { count, dmgMult } = game.mult(s.count);
    for (let i = 0; i < count; i++) {
      game.spawnProjectile({
        kind: 'orbit', x: p.x, y: p.y, r: 34,
        dmg: game.baseDamage(s.damage) * dmgMult, pierce: -1, rehit: 0.3,
        life: s.cooldown + 0.1, color: '#b98cff', knockback: 130,
        orbitAngle: (i / count) * TAU, orbitRadius: s.orbitRadius, orbitSpeed: s.orbitSpeed,
        weaponId: 'blacksun',
      });
    }
  },
});

// ---------------------------------------------------------------- Prism Lance
defineWeapon({
  id: 'prismlance', name: 'Prism Lance', icon: '⟡', tags: ['weapon'], ultra: true,
  desc: 'ULTRA — a relentless sweep of hypersonic light lances that pierce the field.',
  maxLevel: 6,
  stats(l) {
    return {
      cooldown: Math.max(0.14, 0.30 - l * 0.02),
      damage: 13 + l * 4.5,
      count: 2 + Math.floor(l / 2),
      speed: 660,
      pierce: 4 + Math.floor(l / 2),
    };
  },
  fire(game, s) {
    const p = game.player;
    const { count, dmgMult } = game.mult(s.count);
    const sweep = game.elapsed * 2.6;             // continuous rotation
    const col = CONFIG.colors[p.element] || CONFIG.colors.neutral;
    for (let i = 0; i < count; i++) {
      const a = sweep + (i / count) * TAU;
      const sp = s.speed * p.projSpeedMult;
      game.spawnProjectile({
        kind: 'bolt', x: p.x, y: p.y, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp,
        r: 7, drawScale: 1.7, dmg: game.baseDamage(s.damage) * dmgMult,
        pierce: s.pierce, life: 1.3, color: col, knockback: 55, weaponId: 'prismlance',
      });
    }
  },
});
